/**
 * Auth contract VALUES — roles and what a role may reach.
 *
 * `auth.types.ts` next to this file is types-only by construction (the SPA
 * project pulls it in through a glob and must never receive Worker runtime
 * code); a role list and a path matcher are values, so they live here instead,
 * in the dependency-free style `models.contract.ts` established. The file has
 * NO imports on purpose: it is compiled by all three TypeScript projects — the
 * Worker (`tsconfig.worker.json`), the SPA (`tsconfig.app.json`, via the
 * `src/worker/api/*.contract.ts` glob) and the Node tooling
 * (`tsconfig.node.json`, for `tools/seed-user.ts` and the Drizzle schema) —
 * and anything it imported would have to resolve in all three.
 *
 * It is deliberately the ONE place that answers "what is this account not
 * allowed to see", because that question is asked in five places that must
 * never drift apart:
 *
 *   1. the Worker's role gate            — src/worker/index.ts (the enforcement)
 *   2. the dashboard payload             — src/worker/api/dashboard.ts (what is
 *                                          computed at all, not just returned)
 *   3. the sidebar                       — src/app/lib/nav.ts
 *   4. the SPA route guards              — src/app/router.tsx
 *   5. `pnpm seed:user --role`           — tools/seed-user.ts, the only way a
 *                                          role is ever set (it follows `ROLES`;
 *                                          the other four read the table below)
 *
 * A module whose whole surface sits under a path prefix is one entry in
 * `GUARDED_AREAS` below and nothing else — gate, sidebar and route guards all
 * derive from it rather than naming a role of their own. Anything that serves
 * MIXED data (the dashboard payload, the orders module) has to consult the
 * area's capability itself as well; see the note under the table.
 */

/* ── Roles ──────────────────────────────────────────────────────────────────── */

/**
 * The whole role vocabulary, widest first.
 *
 *   admin       co-owner: everything, which is what every account did before
 *               roles existed.
 *   purchasing  the buyer: the operational screens plus the satınalma module —
 *               purchase orders, supplier exposure, the purchasing tile on the
 *               panel, and purchase orders inside the siparişler list. No cari:
 *               what we owe a supplier is not the same question as what a
 *               customer owes us, and the ledger is the owners' business.
 *   staff       employee: the operational screens (stok, siparişler, üretim,
 *               modeller) but no money at all.
 *
 * Also the source of the Drizzle column's enum (`src/db/schema/app.ts`), so the
 * database check and the code cannot disagree.
 */
export const ROLES = ['admin', 'purchasing', 'staff'] as const

export type Role = (typeof ROLES)[number]

/**
 * What a row gets when nobody says otherwise — the column default in
 * `app.users` and the default of `seed:user --role`. Least privilege: a new
 * account that someone forgot to think about sees no money.
 */
export const DEFAULT_ROLE: Role = 'staff'

/**
 * What the SPA assumes while the session is still unknown, so a screen that
 * renders before the answer arrives shows the smaller menu rather than links
 * that 403 (see components/app-shell.tsx and features/auth/api.ts).
 *
 * Deliberately NOT `DEFAULT_ROLE`, even though the two are the same token
 * today: that one is account-creation policy (the column default, the default
 * of `seed:user --role`) and could legitimately be changed to `admin` one day,
 * which must not quietly turn the client-side fallbacks permissive.
 */
export const LEAST_PRIVILEGED_ROLE: Role = 'staff'

/** Narrows unvalidated input: a CLI flag, or a text column read back as string. */
export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/* ── Capabilities ───────────────────────────────────────────────────────────── */

/*
 * One predicate per guarded area, never a bare `role === 'admin'` at the call
 * site. The roles are NOT a ladder — `purchasing` sits above `staff` on one
 * axis and level with it on the other — so "is this role at least X" is a
 * question with no answer here, and every call site has to name the thing it
 * actually needs.
 */

/** Cari: customer/supplier ledgers, balances, statements. Owners only. */
export function canViewCari(role: Role): boolean {
  return role === 'admin'
}

/**
 * Satınalma: purchase orders, supplier exposure — and the purchase orders
 * living in the orders module, which are the same `Siparis_Kay` records reached
 * through another door (see `typeForRole` in ./orders.ts).
 */
export function canViewPurchasing(role: Role): boolean {
  return role === 'admin' || role === 'purchasing'
}

/**
 * Finans: the cheque book as a maturity calendar, data freshness, and the
 * cash-flow screens that will grow next to them. Owners only.
 *
 * Identical to `canViewCari` today — a coincidence, not a relationship: cari
 * is the ledger, finans is the cash position, and either could be widened
 * without the other. A mixed endpoint serving a finans block (a dashboard
 * tile, say) must consult THIS predicate and skip the query it guards;
 * substituting the cari predicate would keep working right up until one of
 * the two changes, and then leak.
 */
export function canViewFinance(role: Role): boolean {
  return role === 'admin'
}

/**
 * Saya (modelhane): entering a scanner calibration by hand.
 *
 * The design is explicit that manual calibration is an ADMIN RECOVERY TOOL and
 * not an operator step — the real figure comes from a verified scanner profile
 * (calibration sheet, locked preset), and until that exists a hand-typed
 * px-per-mm is the only thing standing between a scan and the millimetres the
 * cutter will believe. Without this gate any signed-in account could assert an
 * arbitrary scale on an upload and approval would read it as calibrated, which
 * turns "may digitize" into "may decide how big the parts are".
 *
 * Digitizing itself stays open: an operator uploads, traces and labels exactly
 * as before, and an uncalibrated draft is a legitimate draft — it simply cannot
 * be approved, which is the gate the design already draws.
 */
export function canCalibrateScan(role: Role): boolean {
  return role === 'admin'
}

/* ── What each area covers ──────────────────────────────────────────────────── */

/**
 * A module that is not open to everyone: the paths it owns on both sides, and
 * the one predicate that decides who gets in.
 *
 * Prefixes rather than route lists: every route a module registers lives under
 * one of them — the `/export` CSV routes and `/summary` included — so the
 * Worker's gate does not have to know the route table.
 *
 * `id` is `string` and not the narrowed union so the interface can be written
 * before `GUARDED_AREAS` derives `GuardedAreaId` from it; the literal type is
 * what call sites see, because the table is `as const`.
 */
export interface GuardedArea {
  /** Stable identifier, for tests and diagnostics; never shown to a user. */
  readonly id: string
  /** Worker path prefixes this area owns. */
  readonly apiPrefixes: readonly string[]
  /** The same module's SPA paths — sidebar entries and route guards. */
  readonly appPaths: readonly string[]
  /** Who may reach any of the above. */
  readonly can: (role: Role) => boolean
}

/**
 * The table. Worker gate, SPA router, sidebar and dashboard all read this — an
 * area's paths and its capability are stated once, side by side, so a module
 * cannot end up gated against the wrong predicate on one side.
 */
export const GUARDED_AREAS = [
  {
    id: 'cari',
    apiPrefixes: ['/api/cari'],
    appPaths: ['/cari'],
    can: canViewCari,
  },
  {
    id: 'purchasing',
    apiPrefixes: ['/api/satinalma'],
    appPaths: ['/satinalma'],
    can: canViewPurchasing,
  },
  {
    id: 'finance',
    apiPrefixes: ['/api/finans'],
    appPaths: ['/finans'],
    can: canViewFinance,
  },
] as const satisfies readonly GuardedArea[]

export type GuardedAreaId = (typeof GUARDED_AREAS)[number]['id']

/*
 * Two places apply an area's capability WITHOUT living under one of its
 * prefixes, because they serve mixed data that cannot simply be refused
 * wholesale:
 *
 *   - `api/dashboard.ts` — one payload for every role, minus the blocks the
 *     role cannot have (which are not computed at all for it);
 *   - `api/orders.ts` — `Siparis_Kay` holds sales AND purchase orders, so the
 *     module refuses `type=A` and narrows `type=all` for an account without
 *     `canViewPurchasing`, and 403s a purchase order's detail. Without that,
 *     satınalma's records would still be one query parameter away.
 *
 * Anything new that serves money must do one of the two — join the table above,
 * or consult the capability itself.
 *
 * `api/saya.ts` is a third, different case: the module is open to every
 * session — digitizing is shop-floor work, by owner decision (14.08.2026) —
 * and it is not money, so it is not an area. Its one admin-only act is scan
 * calibration (`canCalibrateScan`), consulted inside the handler.
 */

/**
 * Segment-safe prefix match: `/api/cari` covers `/api/cari` itself and anything
 * below it, and nothing else. A plain `startsWith` would also swallow
 * `/api/caribbean` — no such route exists today, but a gate that depends on
 * that staying true is a gate that opens by accident later.
 *
 * Korgun's `CKod` carries dots and spaces, so the browser sends
 * `/api/cari/120%2001%200013` — but the gate never sees that form: Hono's
 * `getPath` runs `decodeURI` over any path containing `%` before the middleware
 * does, so `c.req.path` is already `/api/cari/120 01 0013`. That is what makes
 * matching the string as-is safe: `decodeURI` deliberately leaves the reserved
 * characters alone, `%2F` included, so no segment can grow a separator, and the
 * gate and the router are guaranteed to be looking at the identical string.
 */
export function isPathUnder(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`)
}

/** The area a Worker path belongs to, or null when the path is open to all. */
export function guardedAreaForApiPath(path: string): GuardedArea | null {
  return (
    GUARDED_AREAS.find((area) => area.apiPrefixes.some((prefix) => isPathUnder(path, prefix))) ??
    null
  )
}

/** The area an SPA path belongs to, or null when the screen is open to all. */
export function guardedAreaForAppPath(path: string): GuardedArea | null {
  return (
    GUARDED_AREAS.find((area) => area.appPaths.some((prefix) => isPathUnder(path, prefix))) ?? null
  )
}

/** May this role reach this Worker path? Ungated paths are always true. */
export function canAccessApiPath(role: Role, path: string): boolean {
  const area = guardedAreaForApiPath(path)
  return area === null || area.can(role)
}

/** May this role reach this SPA path? Ungated screens are always true. */
export function canAccessAppPath(role: Role, path: string): boolean {
  const area = guardedAreaForAppPath(path)
  return area === null || area.can(role)
}

/**
 * Body of the Worker's 403 — the same sentence for every guarded area, because
 * naming the module would tell an account what it is missing without giving it
 * any way to act on that. User-facing, therefore Turkish: the SPA prints
 * whatever `error` the API sent (see `errorMessage()` in src/app/lib/api.ts),
 * so this is what a user reads if a deep link ever races the client-side guard.
 */
export const GUARDED_AREA_FORBIDDEN_MESSAGE = 'Bu bölüme erişim yetkiniz yok.'
